import {
	END_OF_LIFE_OPTIONS,
	MANUFACTURING_REGIONS,
	MATERIALS,
	STAGE_META,
	TRANSPORT_MODES,
	WASH_TEMPERATURES,
} from "./apparelCarbonData.js";

const safeNumber = (value, fallback = 0) => {
	const number = Number(value);
	return Number.isFinite(number) ? number : fallback;
};

const positive = (value, fallback = 0) => Math.max(0, safeNumber(value, fallback));

const findByValue = (items, value) => items.find((item) => item.value === value) || items[0];

export const round = (value, places = 2) => {
	const multiplier = 10 ** places;
	return Math.round((safeNumber(value) + Number.EPSILON) * multiplier) / multiplier;
};

export const getMaterialTotal = (scenario) =>
	(scenario.materials || []).reduce((total, row) => total + positive(row.percentage), 0);

export function calculateFootprint(scenario) {
	const finishedWeightKg = positive(scenario.weightGrams) / 1000;
	const cuttingWasteRate = Math.min(0.8, positive(scenario.cuttingWastePercent) / 100);
	const materialTotal = getMaterialTotal(scenario);
	const normalizedTotal = materialTotal > 0 ? materialTotal : 100;
	const inputWeightKg = finishedWeightKg / Math.max(0.2, 1 - cuttingWasteRate);

	const materialBreakdown = (scenario.materials || []).map((row) => {
		const material = findByValue(MATERIALS, row.material);
		const share = positive(row.percentage) / normalizedTotal;
		const activity = inputWeightKg * share;
		const emissions = activity * material.factor;
		return {
			id: material.value,
			label: material.label,
			percentage: positive(row.percentage),
			activityKg: round(activity, 4),
			factor: material.factor,
			emissions: round(emissions, 4),
			color: material.color,
		};
	});

	const materialsEmission = materialBreakdown.reduce((sum, item) => sum + item.emissions, 0);
	const region = findByValue(MANUFACTURING_REGIONS, scenario.manufacturingRegion);
	const manufacturingEnergy = finishedWeightKg * positive(scenario.manufacturingEnergyKwhPerKg);
	const manufacturingEmission = manufacturingEnergy * region.gridFactor;

	const transport = findByValue(TRANSPORT_MODES, scenario.transportMode);
	const transportTonneKm = (finishedWeightKg / 1000) * positive(scenario.transportDistanceKm);
	const transportEmission = transportTonneKm * transport.factor;

	const lifetimeWears = Math.max(1, positive(scenario.lifetimeWears, 1));
	const wearsBetweenWashes = Math.max(1, positive(scenario.wearsBetweenWashes, 1));
	const washCycles = lifetimeWears / wearsBetweenWashes;
	const wash = findByValue(WASH_TEMPERATURES, scenario.washTemperature);
	const gridFactor = positive(scenario.usePhaseGridFactor, 0.38);
	const dryerShare = Math.min(1, positive(scenario.dryerSharePercent) / 100);
	const washEnergy = washCycles * wash.energyKwh;
	const dryerEnergy = washCycles * dryerShare * 2;
	const careConsumables = washCycles * 0.02;
	const useEmission = (washEnergy + dryerEnergy) * gridFactor + careConsumables;

	const endOfLife = findByValue(END_OF_LIFE_OPTIONS, scenario.endOfLife);
	const endOfLifeEmission = finishedWeightKg * endOfLife.factor;

	const rawStages = {
		materials: materialsEmission,
		manufacturing: manufacturingEmission,
		transport: transportEmission,
		use: useEmission,
		endOfLife: endOfLifeEmission,
	};

	const stages = STAGE_META.map((stage) => ({
		...stage,
		emissions: round(rawStages[stage.id], 4),
	}));
	const total = stages.reduce((sum, stage) => sum + stage.emissions, 0);
	const biggestStage = [...stages].sort((a, b) => b.emissions - a.emissions)[0];

	const calculationRows = [
		{
			stage: "Raw materials",
			activity: `${round(inputWeightKg, 3)} kg input fiber`,
			factor: "Weighted material factors",
			result: materialsEmission,
			explanation: `${round(cuttingWasteRate * 100, 0)}% cutting loss means more fiber enters the system than remains in the garment.`,
		},
		{
			stage: "Manufacturing",
			activity: `${round(manufacturingEnergy, 2)} kWh`,
			factor: `${region.gridFactor} kg CO2e/kWh`,
			result: manufacturingEmission,
			explanation: "Garment mass x process energy intensity x electricity factor.",
		},
		{
			stage: "Transport",
			activity: `${round(transportTonneKm, 3)} tonne-km`,
			factor: `${transport.factor} kg CO2e/tonne-km`,
			result: transportEmission,
			explanation: "Garment mass in tonnes x distance x transport-mode factor.",
		},
		{
			stage: "Use phase",
			activity: `${round(washCycles, 1)} washes; ${round(washEnergy + dryerEnergy, 1)} kWh`,
			factor: `${gridFactor} kg CO2e/kWh + care products`,
			result: useEmission,
			explanation: "Lifetime washing, drying, electricity, and a small care-consumables allowance.",
		},
		{
			stage: "End of life",
			activity: `${round(finishedWeightKg, 3)} kg garment`,
			factor: `${endOfLife.factor} kg CO2e/kg`,
			result: endOfLifeEmission,
			explanation: `${endOfLife.label}; no avoided-product credit is applied.`,
		},
	];

	return {
		finishedWeightKg: round(finishedWeightKg, 4),
		inputWeightKg: round(inputWeightKg, 4),
		materialTotal: round(materialTotal, 2),
		isMaterialMixValid: Math.abs(materialTotal - 100) < 0.01,
		materialBreakdown,
		stages,
		calculationRows,
		total: round(total, 3),
		perWear: round(total / lifetimeWears, 3),
		washCycles: round(washCycles, 1),
		biggestStage,
	};
}

export function compareFootprints(baseline, current) {
	const baselineTotal = positive(baseline.total);
	const currentTotal = positive(current.total);
	const savings = baselineTotal - currentTotal;
	const reductionPercent = baselineTotal > 0 ? (savings / baselineTotal) * 100 : 0;
	return {
		savings: round(savings, 3),
		reductionPercent: round(reductionPercent, 1),
		isLower: currentTotal < baselineTotal,
		challengeComplete: reductionPercent >= 30,
	};
}

export function getHotspotHint(stageId) {
	const hints = {
		materials: "Try a lower-impact fiber, reduce cutting waste, or use less material.",
		manufacturing: "Try cleaner electricity or lower process energy intensity.",
		transport: "Try sea or rail freight, a shorter route, or avoid air freight.",
		use: "Wash less often, wash cold, line dry, or extend the garment life.",
		endOfLife: "Reuse and textile recycling are modeled with lower processing emissions here.",
	};
	return hints[stageId] || "Change one assumption and observe the chart.";
}
